import { useState } from 'react'
import { Eye, ListChecks, Share2, TrendingUp } from 'lucide-react'
import { PageHeader } from '@/components/page-header'
import { PeriodSelector } from '@/components/period-selector'
import { KpiCard } from '@/components/kpi-card'
import { RevenueLineChart } from '@/components/line-chart'
import { DataTable } from '@/components/data-table'
import { SettlementCard } from '@/components/settlement-card'
import { DataLoadError } from '@/components/data-load-error'
import { DataUnavailable } from '@/components/data-unavailable'
import { Button } from '@/components/ui/button'
import { useRevenueData } from '@/hooks/use-revenue-data'
import type { StudioReviewUnavailableReasonCode } from '@/hooks/dashboard-data-state'
import type { StudioReviewDataClient } from '@/services/dashboard-api'
import { useStudioStore } from '@/store/studio-store'
import { pushStudioRoute } from '@/lib/studio-navigation'
import type { PeriodFilter } from '@/types/dashboard'
import { DashboardContainer } from '../components/dashboard-container'
import { OperationsTabs } from '../components/operations-tabs'
import { ReviewObservationDisclosure } from '../components/review-observation-disclosure'
import { useStudioLocale } from '@/i18n'
import type { StudioDictionary } from '@/i18n/studio-dictionaries'

const KPI_ICONS = [
  <Eye key="views" className="h-4 w-4 text-muted-foreground" />,
  <TrendingUp key="trend" className="h-4 w-4 text-muted-foreground" />,
  <ListChecks key="review" className="h-4 w-4 text-muted-foreground" />,
  <Share2 key="share" className="h-4 w-4 text-muted-foreground" />,
]

type ImpactCopy = StudioDictionary['businessScreens']['impact']

/** Column headers are words on the screen, so they follow the active locale
 *  rather than being frozen at module scope. */
function columnsFor(copy: ImpactCopy) {
  return [
    { key: 'app_name' as const, header: copy.columns.artifact },
    { key: 'revenue' as const, header: copy.columns.views, align: 'right' as const, render: (v: unknown) => (v as number).toLocaleString() },
    { key: 'orders' as const, header: copy.columns.reviews, align: 'right' as const },
    { key: 'conversion_rate' as const, header: copy.columns.cvr, align: 'right' as const, render: (v: unknown) => (v as number) > 0 ? `${v}%` : '—' },
  ]
}

interface RevenuePageProps {
  dataClient?: StudioReviewDataClient
}

export function RevenuePage({ dataClient }: RevenuePageProps = {}) {
  const [period, setPeriod] = useState<PeriodFilter>('7d')
  const { kpis, timeSeries, appRevenue, settlement, reload } = useRevenueData(period, dataClient)
  const setStudioRoute = useStudioStore((state) => state.setStudioRoute)
  const { strings } = useStudioLocale()
  const copy = strings.businessScreens.impact
  const reviewCopy = strings.businessScreens.review
  const pageState = kpis.status === 'ready' ? kpis.reviewState ?? 'error' : kpis.status

  function goToShare() {
    setStudioRoute('apps-live')
    pushStudioRoute('apps-live')
  }

  let content: React.ReactNode
  switch (pageState) {
    case 'loading':
      content = (
        <div className="animate-pulse space-y-4" role="status">
          <p className="text-sm text-muted-foreground">{copy.loading}</p>
          <div className="h-48 rounded-xl bg-muted" aria-hidden="true" />
        </div>
      )
      break
    case 'measured':
      content = (
        <>
          <ReviewObservationDisclosure observedAt={kpis.observedAt as string} evidenceRefs={kpis.evidenceRefs} scope={copy.scope} />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {kpis.data?.map((kpi, index) => (
              <KpiCard key={kpi.label} title={kpi.label} value={kpi.value} trend={kpi.trend} trendLabel={kpi.trendLabel} icon={KPI_ICONS[index]} />
            ))}
          </div>
          <RevenueLineChart data={timeSeries.data as object[]} xKey="date" series={[{ key: 'value', label: copy.seriesViews, color: 'hsl(var(--primary))' }]} title={copy.trendTitle} />
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground">{copy.byArtifact}</h2>
            <DataTable columns={columnsFor(copy)} data={appRevenue.data as NonNullable<typeof appRevenue.data>} />
          </div>
          {settlement.data !== null && <SettlementCard settlement={settlement.data} />}
        </>
      )
      break
    case 'empty':
      content = (
        <div className="rounded-xl border border-dashed border-border bg-card p-6" role="status">
          <p className="text-sm font-medium text-foreground">{copy.empty}</p>
          <ReviewObservationDisclosure observedAt={kpis.observedAt as string} evidenceRefs={kpis.evidenceRefs} scope={copy.scope} state="empty" />
        </div>
      )
      break
    case 'unavailable': {
      const guidance = copy.unavailable[kpis.unavailableReasonCode as StudioReviewUnavailableReasonCode]
      content = (
        <>
          <DataUnavailable summary={guidance.summary} message={kpis.unavailableReason as string} testId="revenue-unavailable" />
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-foreground">{guidance.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{guidance.detail}</p>
            <Button variant="outline" size="sm" className="mt-4 min-h-10" onClick={goToShare}>{reviewCopy.openSurface(strings.navigation.share)}</Button>
          </section>
        </>
      )
      break
    }
    case 'error':
      content = <DataLoadError message={copy.loadFailed} onRetry={reload} testId="revenue-kpis-error" />
      break
  }

  return (
    <DashboardContainer className="py-6 space-y-6">
      <OperationsTabs activeRoute="revenue" />
      <PageHeader
        route="revenue"
        title={strings.navigation.impact}
        description={copy.description}
        action={<PeriodSelector value={period} onChange={setPeriod} />}
      />
      {content}
    </DashboardContainer>
  )
}
